import { ListItemIcon, ListItemText } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { DeleteThin } from '@vy/ui-design-system';
import { awaitAllSafely } from '@vy/promise';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useDeleteContract } from '../../../../../hooks/requests/use-delete-contract';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { runMutation } from '../../../../../utils/vy-mutation';
import {
  invalidateContractsQueries,
  invalidateCustomersQueries,
  invalidateBillingRevenueQueries,
  invalidatePlansQueries,
} from '../../../../../utils/query-invalidation';

type DeleteContractMenuItemProps = {
  contractId: string;
  disabled?: boolean;
  contractName?: string;
};

export const DeleteContractMenuItem: React.FC<DeleteContractMenuItemProps> = ({
  contractId,
  disabled,
  contractName,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const navigate = useVyNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const deleteContractMutation = useDeleteContract({
    onSuccess: async () => {
      const redirectPath = location.pathname.startsWith('/customers')
        ? `${location.pathname}${location.search}`
        : '/plans?tab=contracts';

      queryClient.removeQueries({
        predicate: (query) => query.queryKey.includes(contractId),
      });

      await awaitAllSafely(
        invalidateContractsQueries(queryClient),
        invalidateCustomersQueries(queryClient),
        invalidateBillingRevenueQueries(queryClient),
        invalidatePlansQueries(queryClient),
      );

      navigate(redirectPath, { replace: true });
    },
  });

  const handleDialogSubmit = async () => {
    handleClose();
    await runMutation(deleteContractMutation, contractId, {
      pending: 'Deleting...',
      success: 'Deleted',
      error: 'Failed to delete contract',
    });
  };

  return (
    <>
      <MenuItem disabled={disabled} onClick={handleOpen}>
        <ListItemIcon>
          <DeleteThin />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>

      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`Delete ${contractName}`}
        labelDescription={`Are you sure you want to delete ${contractName}?`}
        warningText="This action is final and cannot be reversed"
        cancelButtonText="Cancel"
        ctaText="Delete contract"
        ctaDisabled={deleteContractMutation.isLoading}
        onSubmit={handleDialogSubmit}
      />
    </>
  );
};
